/**
 * Web widget mobile preview iframe.
 * Renders the widget inside an iframe with a real 440px viewport so SDK/media queries
 * see the correct viewport. Desktop rendering and other channels are unchanged.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import type { IframeInitConfig } from 'context/WidgetSdkContext';

const MOBILE_VIEWPORT_WIDTH = 440;

const MESSAGE_TYPE_RENDER = 'WEB_WIDGET_RENDER';
const MESSAGE_TYPE_RENDER_DONE = 'WEB_WIDGET_RENDER_DONE';
const MESSAGE_TYPE_IFRAME_READY = 'WEB_WIDGET_IFRAME_READY';

/** Same-origin static page so the SDK script loads with app origin (same as desktop). */
const WIDGET_MOBILE_PREVIEW_SRC = `${process.env.PUBLIC_URL || ''}/widget-mobile-preview.html`;

export interface WebWidgetMobileIframePayload {
  sdkScriptUrl: string;
  scenarioId: string;
  widgetTag: string;
  /** Required: SDK must be initialized in the iframe before renderWidget. */
  initConfig: IframeInitConfig | null;
}

export type WebWidgetMobileIframeRenderStatus = 'idle' | 'loading' | 'success' | 'error';

export interface WebWidgetMobileIframeProps {
  sdkScriptUrl: string | null;
  scenarioId: string | null;
  widgetTag: string | null;
  /** Init config from parent (required for iframe to initialize SDK before render). */
  iframeInitConfig: IframeInitConfig | null;
  displayName?: string;
  /** When true, show "Initializing…" instead of "Initialize on desktop first" while waiting for init. */
  isSdkInitializing?: boolean;
  /** When false and no init config, treat as "still loading" and show Initializing… */
  isSdkReady?: boolean;
  /** Called when iframe finishes render (success or error). Use to show loading state in parent. */
  onRenderComplete?: (success: boolean, error?: string) => void;
}

export const WebWidgetMobileIframe: React.FC<WebWidgetMobileIframeProps> = ({
  sdkScriptUrl,
  scenarioId,
  widgetTag,
  iframeInitConfig,
  displayName = 'Web Widget',
  isSdkInitializing = false,
  isSdkReady = true,
  onRenderComplete,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeReadyRef = useRef(false);

  const [renderStatus, setRenderStatus] = React.useState<WebWidgetMobileIframeRenderStatus>('idle');
  const [renderError, setRenderError] = React.useState<string | null>(null);

  const sendRender = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win || !sdkScriptUrl || !scenarioId || !widgetTag || !iframeInitConfig) return;
    setRenderStatus('loading');
    setRenderError(null);
    win.postMessage(
      {
        type: MESSAGE_TYPE_RENDER,
        payload: {
          sdkScriptUrl,
          scenarioId,
          widgetTag,
          initConfig: iframeInitConfig,
        } as WebWidgetMobileIframePayload,
      },
      '*'
    );
  }, [sdkScriptUrl, scenarioId, widgetTag, iframeInitConfig]);

  // Safety timeout: if we stay in loading for too long, show error (e.g. SDK URL returned HTML → "Unexpected token '<'" in console)
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (renderStatus !== 'loading') return;
    const LOAD_TIMEOUT_MS = 18000;
    loadTimeoutRef.current = setTimeout(() => {
      loadTimeoutRef.current = null;
      setRenderStatus('error');
      setRenderError(
        "Load timed out. If the console shows \"Unexpected token '<'\", the SDK URL may be returning an HTML page (404/CORS). Try desktop preview first."
      );
      onRenderComplete?.(false, 'Load timed out');
    }, LOAD_TIMEOUT_MS);
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };
  }, [renderStatus, onRenderComplete]);

  const canRender = Boolean(sdkScriptUrl && scenarioId && widgetTag && iframeInitConfig);
  const lastSentPayloadRef = useRef<string | null>(null);
  const getPayloadKey = useCallback(() => {
    if (!iframeInitConfig) return null;
    return [sdkScriptUrl, scenarioId, widgetTag, iframeInitConfig.scenarioId].join('|');
  }, [sdkScriptUrl, scenarioId, widgetTag, iframeInitConfig]);

  // Listen for READY from iframe (so we send payload only after iframe listener is set) and RENDER_DONE
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const iframeWin = iframeRef.current?.contentWindow;
      if (iframeWin && event.source !== iframeWin) return;
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === MESSAGE_TYPE_IFRAME_READY) {
        iframeReadyRef.current = true;
        const key = getPayloadKey();
        if (key) lastSentPayloadRef.current = key;
        sendRender();
      }
      if (event.data.type === MESSAGE_TYPE_RENDER_DONE) {
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
          loadTimeoutRef.current = null;
        }
        const success = Boolean(event.data.success);
        const error = event.data.error;
        setRenderStatus(success ? 'success' : 'error');
        setRenderError(error || null);
        onRenderComplete?.(success, error);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sendRender, onRenderComplete, getPayloadKey]);

  // When props change after iframe was ready, send new payload (e.g. template/scenario change). Skip if we already sent this payload (e.g. from READY handler).
  useEffect(() => {
    if (!iframeReadyRef.current || !canRender) return;
    const key = getPayloadKey();
    if (key && key === lastSentPayloadRef.current) return;
    lastSentPayloadRef.current = key;
    sendRender();
  }, [sendRender, canRender, getPayloadKey]);

  const showLoadingOverlay = canRender && (renderStatus === 'idle' || renderStatus === 'loading');

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        position: 'relative',
      }}
    >
      {!canRender && (
        <Box sx={{ padding: 2, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            {!sdkScriptUrl
              ? `Loading ${displayName}…`
              : !iframeInitConfig
                ? (isSdkInitializing || !isSdkReady
                    ? `Loading ${displayName}…`
                    : 'Initialize widget on desktop first, then switch to mobile preview.')
                : !widgetTag
                  ? 'No widget mapping. Select a template.'
                  : `Loading ${displayName}…`}
          </Typography>
        </Box>
      )}
      {showLoadingOverlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading {displayName}…
          </Typography>
        </Box>
      )}
      {canRender && renderStatus === 'error' && (
        <Box sx={{ padding: 1.5, textAlign: 'center', color: 'error.main' }}>
          <Typography variant="caption" component="span">
            Widget failed to load in mobile preview.
            {renderError && ` ${renderError}`}
          </Typography>
        </Box>
      )}
      <iframe
        ref={iframeRef}
        src={WIDGET_MOBILE_PREVIEW_SRC}
        title="Web widget mobile preview"
        onLoad={() => {
          iframeReadyRef.current = false;
        }}
        style={{
          width: '100%',
          minWidth: `${MOBILE_VIEWPORT_WIDTH}px`,
          flex: 1,
          minHeight: canRender ? 400 : 0,
          border: 'none',
          display: 'block',
        }}
      />
    </Box>
  );
};
